import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import dayjs from 'dayjs';
import { API_BASE_URL } from '../utils/api';
import Modal from 'react-modal';
import BackButton from '../components/BackButton';
import { toast } from 'react-toastify';
import DataTable from 'react-data-table-component';

const Dashboard = () => {
  const [calendarShifts, setCalendarShifts] = useState({});
  const [shifts, setShifts] = useState([]);
  const [clients, setClients] = useState([]);
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [selectedClient, setSelectedClient] = useState(null);
  const token = localStorage.getItem('token');
  const navigate = useNavigate();

  useEffect(() => {
    const fetchClients = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/api/clients`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setClients(res.data);
      } catch (err) {
        console.error('Failed to load clients');
      }
    };
    if (token) fetchClients();
  }, [token]);

  useEffect(() => {
    const fetchShifts = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/api/shifts`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const data = res.data || [];
        const grouped = {};

        data.forEach((shift) => {
          const date = shift.date;
          if (!grouped[date]) grouped[date] = [];
          grouped[date].push(shift);
        });

        setCalendarShifts(grouped);

        const today = dayjs().format('YYYY-MM-DD');
        setShifts(grouped[today] || []);
        setSelectedDate(dayjs());
      } catch (err) {
        console.error('Error loading shifts:', err);
      }
    };

    if (token) fetchShifts();
  }, [token]);

  const handleDateClick = (day) => {
    const dateStr = day.format('YYYY-MM-DD');
    setSelectedDate(day);
    setShifts(calendarShifts[dateStr] || []);
  };

  // const handleDeleteDocument = async (clientId, filePath) => {
  //   if (!window.confirm('Delete this document?')) return;
  
  //   try {
  //     await axios.delete(`${API_BASE_URL}/api/clients/${clientId}/document`, {
  //       headers: { Authorization: `Bearer ${token}` },
  //       data: { filePath },
  //     });
  
  //     // Remove from state
  //     setSelectedClient((prev) => ({
  //       ...prev,
  //       documents: JSON.stringify(
  //         JSON.parse(prev.documents).filter((d) => d !== filePath)
  //       ),
  //     }));
  
  //     toast.success('Document deleted');
  //   } catch (err) {
  //     toast.error('Failed to delete document');
  //   }
  // };
  const handleDeleteShift = async (id) => {
    if (!window.confirm('Are you sure you want to delete this shift?')) return;

    try {
      await axios.delete(`${API_BASE_URL}/api/shifts/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      toast.success('Shift deleted');

      const updatedShifts = shifts.filter((shift) => shift.id !== id);
      setShifts(updatedShifts);

      const dateStr = selectedDate.format('YYYY-MM-DD');
      const updatedCalendarShifts = { ...calendarShifts };
      updatedCalendarShifts[dateStr] = updatedShifts;
      setCalendarShifts(updatedCalendarShifts);
    } catch (err) {
      console.error(err);
      toast.error('Failed to delete shift');
    }
  };

  const findClient = (clientId) => {
    return clients.find((client) => client.id === clientId);
  };

  const renderCalendarDays = () => {
    const start = selectedDate.startOf('month').startOf('week');
    const end = selectedDate.endOf('month').endOf('week');
    const days = [];
    let day = start;
    const today = dayjs();

    while (day.isBefore(end, 'day')) {
      const currentDay = day;
      const dateStr = currentDay.format('YYYY-MM-DD');
      const shiftsForDay = calendarShifts[dateStr] || [];

      days.push(
        <div
          key={dateStr}
          onClick={() => handleDateClick(currentDay)}
          className={`flex flex-col items-center justify-center border rounded p-0.5 cursor-pointer hover:bg-purple-700 transition-all ${
            currentDay.isSame(selectedDate, 'day')
              ? 'bg-[#8F4EFF] text-white font-bold'
              : 'bg-[#1C1235]'
          } ${currentDay.month() !== selectedDate.month() ? 'opacity-40' : ''} ${
            currentDay.isSame(today, 'day') ? 'border-2 border-blue-400' : ''
          }`}
          style={{ height: '40px', minWidth: '40px' }}
        >
          <span className="text-[10px]">{currentDay.date()}</span>
          {shiftsForDay.length > 0 && (
            <div className="flex items-center gap-0.5 mt-0.5">
              <span className="w-1 h-1 bg-green-400 rounded-full"></span>
              {shiftsForDay.length > 1 && (
                <span className="text-[8px] text-green-400">{shiftsForDay.length}</span>
              )}
            </div>
          )}
        </div>
      );
      day = day.add(1, 'day');
    }
    return days;
  };

  const columns = [
    {
      name: 'Shift Info',
    sortable: true,
    width: '150px',
    cell: (row) => {
      return (
        <span
          className="hover:text-blue-500 cursor-pointer flex items-center gap-1"
          onClick={() => navigate(`/shifts/${row.id}/note`)}
        >
          {row.title || 'Untitled'}
          <span role="img" aria-label="info">🛈</span>
        </span>
      );
    },
  },
  {
    name: 'Client Name',
    sortable: true,
    width: '150px',
    cell: (row) => {
      const client = findClient(row.clientId);
      return (
        <span
          className="hover:text-blue-500 cursor-pointer flex items-center gap-1"
          onClick={() => setSelectedClient(client)}
        >
          {client?.name || 'N/A'}
          <span role="img" aria-label="user">👤</span>
        </span>
      );
    },
  },
    {
      name: 'Start Time',
      selector: (row) => row.startTime,
      sortable: true,
      width: '150px',
    },
    {
      name: 'End Time',
      selector: (row) => row.endTime,
      sortable: true,
      width: '150px',
    },
    {
      name: 'Address',
      width: '380px',
      sortable: false,
      cell: (row) => {
        const client = findClient(row.clientId);
        if (client?.address && client?.latitude && client?.longitude) {
          return (
            <a
              href={`https://www.google.com/maps/search/?api=1&query=${client.latitude},${client.longitude}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-400 underline text-xs hover:text-blue-500"
              style={{ textDecoration: 'none' }}
            >
              {client.address}
            </a>
          );
        } else {
          return <span className="text-gray-400 text-xs">N/A</span>;
        }
      },
    },
    {
      name: 'Actions',
      width: '300px',
      cell: (row) => (
        <div className="flex gap-2">
          <button
            onClick={() => navigate(`/shifts/${row.id}/note`)}
            className="bg-[#8F4EFF] hover:bg-purple-700 text-white px-2 py-1 rounded text-sm"
          >
            + Note
          </button>
          <button
            onClick={() => navigate(`/shifts/${row.id}/edit`)}
            className="bg-blue-500 hover:bg-blue-600 text-white px-2 py-1 rounded text-sm"
          >
            Edit
          </button>
          <button
            onClick={() => handleDeleteShift(row.id)}
            className="bg-red-500 hover:bg-red-600 text-white px-2 py-1 rounded text-sm"
          >
            Delete
          </button>
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    },
  ];
  
  

  return (
    <div className="min-h-screen bg-[#0E0A1F] text-white p-4 relative">
      <BackButton />
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex items-center justify-between mb-2">
          <button onClick={() => setSelectedDate((prev) => prev.subtract(1, 'month'))}>←</button>
          <h2 className="text-lg font-bold">{selectedDate.format('MMMM YYYY')}</h2>
          <button onClick={() => setSelectedDate((prev) => prev.add(1, 'month'))}>→</button>
        </div>

        <div className="grid grid-cols-7 text-center gap-2 text-xs text-gray-400">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
            <div key={d}>{d}</div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-2">{renderCalendarDays()}</div>

        <div className="mt-4">
          <h3 className="text-lg font-bold mb-3">
            {selectedDate.format('DD MMM YYYY')} — Shifts
          </h3>

          <DataTable
            columns={columns}
            data={shifts}
            pagination
            paginationPerPage={10}
            // highlightOnHover
            responsive
            customStyles={{
              headCells: {
                style: {
                  backgroundColor: '#1C1235',
                  color: 'white',
                  fontWeight: 600,
                },
              },
              rows: {
                style: {
                  backgroundColor: '#0E0A1F',
                  color: 'white',
                },
              },
              pagination: {
                style: {
                  backgroundColor: '#1C1235',
                  color: 'white',
                },
              },
            }}
          />
        </div>
        {/* Modal for client detail */}
        {selectedClient && (
  <Modal
    isOpen={true}
    onRequestClose={() => setSelectedClient(null)}
    className="max-w-lg mx-auto mt-24 bg-[#1C1235] p-6 rounded-xl outline-none shadow-xl text-white"
    overlayClassName="fixed inset-0 bg-black bg-opacity-60 flex items-start justify-center z-50"
  >
    <h2 className="text-xl font-bold mb-4">{selectedClient.name}</h2>
    <p><strong>Phone:</strong> {selectedClient.phone}</p>
    <p><strong>Address:</strong> {selectedClient.address}</p>
    <p><strong>DOB:</strong> {selectedClient.dob}</p>
    <p><strong>NDIS:</strong> {selectedClient.ndisNumber}</p>
    <p><strong>About:</strong> {selectedClient.about}</p>
    <p><strong>Allergies:</strong> {selectedClient.allergies}</p>
    <p><strong>Medications:</strong> {selectedClient.medications}</p>
    <p><strong>Emergency Contact:</strong> {selectedClient.emergencyContact}</p>
    <p><strong>Goals:</strong> {selectedClient.goals}</p>
    <p><strong>Other Info:</strong> {selectedClient.otherInfo}</p>
    <p><strong>Plan Type:</strong> {selectedClient.planType}</p>

    {/* ✅ Safely parse and display documents */}
    {selectedClient.documents && (
      <p>
        <strong>Documents : </strong>
        {(Array.isArray(selectedClient.documents)
          ? selectedClient.documents
          : (() => {
              try {
                return JSON.parse(selectedClient.documents);
              } catch {
                return [];
              }
            })()
        ).map((doc, idx, arr) => (
          <a
            key={idx}
            href={`${API_BASE_URL}${doc.startsWith('/') ? '' : '/'}${doc}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-400 hover:text-purple-200"
          >
            Doc {idx + 1}{idx < arr.length - 1 ? ', ' : ''}
          </a>
        ))}
      </p>
    )}


    <div className="text-right mt-4">
      <button
        onClick={() => setSelectedClient(null)}
        className="bg-red-600 hover:bg-red-700 px-4 py-2 rounded"
      >
        Close
      </button>
    </div>
  </Modal>
)}

      </div>

      <button
        className="fixed bottom-6 right-6 w-14 h-14 rounded-full bg-[#8F4EFF] hover:bg-purple-700 text-2xl flex items-center justify-center shadow-lg"
        onClick={() => navigate('/shifts/new')}
        style={{ marginBottom: '30px' }}
      >
        +
      </button>
    </div>
  );
};

export default Dashboard;
